import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './Auth.css';
import './Achievements.css';
import { authCardStyle, authButtonStyle, authStatPanelStyle } from '../constants/authStyles';
import AuthSelectionToolbar from './AuthSelectionToolbar';
import { usePopupMessage } from '../hooks/usePopupMessage';

const Achievements = () => {
  const { t } = useTranslation('achievements');
  const { t: tc } = useTranslation('common');
  const [leaderboard, setLeaderboard] = useState(null);
  const { setError, clearError } = usePopupMessage();
  const navigate = useNavigate();
  const username = localStorage.getItem('username');
  const { t: tsoc } = useTranslation('social');
  const [friendBusy, setFriendBusy] = useState({});
  const [friendSent, setFriendSent] = useState({});
  const [friendMsg, setFriendMsg] = useState('');

  // Sıralamadan arkadaşlık isteği gönderme (istekler Profil > Arkadaşlar'da kabul edilir).
  const addFriend = async (target) => {
    setFriendBusy((b) => ({ ...b, [target]: true }));
    try {
      const res = await axios.post('/api/friends/request', { target });
      setFriendMsg(res.data?.message || '');
      if (res.data?.success) setFriendSent((m) => ({ ...m, [target]: true }));
    } catch (e) {
      setFriendMsg(e.response?.data?.message || tsoc('actionFailed'));
    } finally {
      setFriendBusy((b) => ({ ...b, [target]: false }));
    }
  };
  // Arama: isim veya sıra numarası (ör. "42" ya da "#42"); sunucuda aranır.
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const searchSeq = useRef(0);

  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setSearchResults(null);
      return undefined;
    }
    const seq = ++searchSeq.current;
    const timer = setTimeout(async () => {
      const rankMatch = q.match(/^#?(\d+)$/);
      const params = new URLSearchParams({ username: username || '' });
      if (rankMatch) params.set('rank', rankMatch[1]);
      else params.set('q', q);
      try {
        const res = await axios.get(`/api/leaderboard?${params.toString()}`);
        if (seq !== searchSeq.current) return;
        setSearchResults(res.data?.success ? (res.data.searchResults || []) : []);
      } catch {
        if (seq === searchSeq.current) setSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, username]);

  useEffect(() => {
    if (!username) {
      navigate('/login');
      return;
    }
    fetchLeaderboard();
  }, [username, navigate]);

  const fetchLeaderboard = async () => {
    try {
      clearError();
      const response = await axios.get(`/api/leaderboard?username=${username}`);
      if (response.data.success) {
        setLeaderboard(response.data);
      } else {
        setError(response.data.message || t('errorLoadFailed'));
      }
    } catch (err) {
      setError(err.response?.data?.message || t('errorLoadFailed'));
    }
  };

  const handleBack = () => {
    navigate('/game');
  };

  const buildDisplayRows = () => {
    if (!leaderboard || !leaderboard.topPlayers) return [];

    const topPlayers = leaderboard.topPlayers || [];
    const currentPlayer = leaderboard.currentPlayer || null;
    const lastPlayer = leaderboard.lastPlayer || null;
    const totalPlayers = leaderboard.totalPlayers || 0;

    const rows = [];

    topPlayers.forEach((p) => {
      rows.push({
        type: 'player',
        key: `top-${p.rank}`,
        player: p,
        isCurrent:
          currentPlayer && currentPlayer.rank === p.rank && currentPlayer.username === p.username,
        isLast: lastPlayer && lastPlayer.rank === p.rank && lastPlayer.username === p.username,
      });
    });

    if (totalPlayers <= topPlayers.length) {
      return rows;
    }

    const currentInTop =
      currentPlayer && currentPlayer.rank && currentPlayer.rank <= topPlayers.length;

    if (currentPlayer && !currentInTop) {
      rows.push({ type: 'separator', key: 'sep-current' });
      rows.push({
        type: 'player',
        key: `current-${currentPlayer.rank}`,
        player: currentPlayer,
        isCurrent: true,
        isLast: lastPlayer && lastPlayer.rank === currentPlayer.rank,
      });
    }

    if (
      lastPlayer &&
      lastPlayer.rank > topPlayers.length &&
      (!currentPlayer || lastPlayer.rank !== currentPlayer.rank)
    ) {
      rows.push({ type: 'separator', key: 'sep-last' });
      rows.push({
        type: 'player',
        key: `last-${lastPlayer.rank}`,
        player: lastPlayer,
        isCurrent: currentPlayer && lastPlayer.rank === currentPlayer.rank,
        isLast: true,
      });
    }

    return rows;
  };

  const renderToolbar = () => (
    <AuthSelectionToolbar
      className="achievements-toolbar"
      headerClassName="achievements-header-title"
      onBack={handleBack}
      buttonStyle={authButtonStyle}
      header={
        <>
          <h1>
            <img
              src="/image/menu/basarilar.png"
              alt=""
              className="achievements-title-icon"
              draggable={false}
            />
            {t('title')}
          </h1>
          <p>{t('subtitle')}</p>
        </>
      }
    />
  );

  if (!leaderboard) {
    return (
      <div className="auth-container auth-container--selection">
        <div className="auth-card" style={authCardStyle}>
          {renderToolbar()}
          <p className="achievements-empty-text">{t('empty')}</p>
        </div>
      </div>
    );
  }

  const searching = searchResults !== null;
  const rows = searching
    ? searchResults.map((p) => ({
      type: 'player',
      key: `search-${p.rank}-${p.username}`,
      player: p,
      isCurrent: p.username === username,
      isLast: false,
    }))
    : buildDisplayRows();
  const { totalPlayers, currentPlayer } = leaderboard;

  return (
    <div className="auth-container auth-container--selection">
      <div className="auth-card" style={authCardStyle}>
        {renderToolbar()}

        <div className="achievements-summary">
          <div className="summary-item achievements-stat-panel" style={authStatPanelStyle}>
            <span className="summary-label">{t('totalPlayers')}</span>
            <span className="summary-value">{totalPlayers}</span>
          </div>
          <div className="summary-item achievements-stat-panel" style={authStatPanelStyle}>
            <span className="summary-label">{t('yourRank')}</span>
            <span className="summary-value">
              {currentPlayer ? t('rankValue', { rank: currentPlayer.rank }) : t('characterNotFound')}
            </span>
          </div>
        </div>

        <div className="achievements-search">
          <input
            type="search"
            inputMode="search"
            className="achievements-search-input"
            placeholder={t('searchPlaceholder')}
            aria-label={t('searchPlaceholder')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button
              type="button"
              className="achievements-search-clear"
              aria-label={t('searchClear')}
              onClick={() => setQuery('')}
            >
              ×
            </button>
          )}
        </div>

        {friendMsg ? <p className="leaderboard-friend-msg" role="status">{friendMsg}</p> : null}
        <div className="leaderboard-section">
          <div className="leaderboard-header-row">
            <div className="col-rank">{t('colRank')}</div>
            <div className="col-player">{t('colPlayer')}</div>
            <div className="col-level-gold">{t('colLevelGold')}</div>
          </div>
          <div className="leaderboard-rows">
            {searching && rows.length === 0 && (
              <p className="achievements-empty-text">{t('searchNoResults')}</p>
            )}
            {rows.map((row) => {
              if (row.type === 'separator') {
                return (
                  <div key={row.key} className="leaderboard-separator">
                    ...
                  </div>
                );
              }

              const { player, isCurrent, isLast } = row;
              const rowClassNames = [
                'leaderboard-row',
                isCurrent ? 'current-player-row' : '',
                isLast ? 'last-player-row' : '',
              ]
                .filter(Boolean)
                .join(' ');

              return (
                <div key={row.key} className={rowClassNames}>
                  <div className="col-rank">#{player.rank}</div>
                  <div className="col-player">
                    <div className="player-username">
                      {player.username}
                      {player.username !== username && !isCurrent ? (
                        <button
                          type="button"
                          className="leaderboard-add-friend"
                          disabled={Boolean(friendBusy[player.username]) || friendSent[player.username]}
                          onClick={() => addFriend(player.username)}
                          title={tsoc('addFriend')}
                        >
                          {friendSent[player.username] ? '✓' : tsoc('addFriendShort')}
                        </button>
                      ) : null}
                    </div>
                    <div className="player-character">{player.characterName}</div>
                  </div>
                  <div className="col-level-gold">
                    <div className="player-level">{tc('level')} {player.level}</div>
                    <div className="player-gold">{player.gold} {tc('yang')}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Achievements;
